import { useEffect } from "react";
import ProductGrid from "@/components/ProductGrid";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import QRCode from "qrcode.react";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { HeartHandshake } from "lucide-react";

const Index = () => {
  const isMobile = useIsMobile();
  useEffect(() => {
    const hasVisited = localStorage.getItem("hasVisitedFlom");
    const today = new Date();
    const isValentinesDay = today.getMonth() === 1 && today.getDate() === 14; // February 14th

    if (!hasVisited) {
      const duration = 3000;
      const animationEnd = Date.now() + duration;
      const defaults = {
        startVelocity: 30,
        spread: 360,
        ticks: 60,
        zIndex: 0
      };
      function randomInRange(min: number, max: number) {
        return Math.random() * (max - min) + min;
      }
      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();
        if (timeLeft <= 0) {
          return clearInterval(interval);
        }
        const particleCount = 50 * (timeLeft / duration);
        confetti({
          ...defaults,
          particleCount,
          origin: {
            x: randomInRange(0.1, 0.9),
            y: randomInRange(0.1, 0.9)
          },
          colors: ['#D6BCFA', '#FDE1D3', '#FFDEE2']
        });
      }, 250);
      toast("Welkom bij FLOM!", {
        description: "Ontdek onze handgemaakte sieraden"
      });
      localStorage.setItem("hasVisitedFlom", "true");
    }
    if (isValentinesDay) {
      const duration = 5000;
      const animationEnd = Date.now() + duration;
      const heartShape = [[0.5, 0.3],
      // Top of heart
      [0.3, 0.1],
      // Left curve
      [0.2, 0.2], [0.5, 0.7],
      // Bottom point
      [0.8, 0.2],
      // Right curve
      [0.7, 0.1]];
      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();
        if (timeLeft <= 0) {
          return clearInterval(interval);
        }
        heartShape.forEach(([x, y]) => {
          confetti({
            particleCount: 3,
            startVelocity: 20,
            spread: 360,
            ticks: 60,
            zIndex: 0,
            origin: {
              x,
              y
            },
            colors: ['#FFDEE2', '#ea384c', '#D946EF'],
            // Soft pink, Red, Magenta pink
            shapes: ['circle']
          });
        });
      }, 400);
      toast("❤️ Fijne Valentijnsdag!", {
        description: "Verras je geliefde met een mooi sieraad",
        duration: 5000
      });
    }
  }, []);
  const whatsappChannelUrl = "https://whatsapp.com/channel/0029VaxuBkQD8SDsDel6Vp1L";
  return <>
      <Helmet>
        <title>FLOM - Handgemaakte Sieraden</title>
        <meta name="description" content="Ontdek onze collectie handgemaakte sieraden. Van elegante armbanden tot schattige oorbellen, elk stuk is uniek en met liefde gemaakt." />
        <meta property="og:title" content="FLOM - Handgemaakte Sieraden" />
        <meta property="og:description" content="Ontdek onze collectie handgemaakte sieraden. Van elegante armbanden tot schattige oorbellen, elk stuk is uniek en met liefde gemaakt." />
        <meta property="og:image" content={`${window.location.origin}/lovable-uploads/afe85108-72bb-46ab-8d23-e47c1361ac05.png`} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-[#4D9DE0]/80 via-[#FF6B9C]/70 to-[#95F985]/80 relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_1000px_at_100%_200px,#0EA5E990,transparent)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_0%_300px,#FFE135A0,transparent)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_600px_at_50%_800px,#D946EFA0,transparent)]"></div>
        
        <div className="absolute top-20 left-4 z-10">
          <HeartHandshake 
            size={60} 
            className="animate-float" 
            strokeWidth={2.5}
            style={{
              stroke: "url(#peace-icon-gradient)"
            }}
          />
          <svg width="0" height="0" className="absolute">
            <defs>
              <linearGradient id="peace-icon-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: "#4D9DE0", stopOpacity: 0.8 }} />
                <stop offset="50%" style={{ stopColor: "#FF6B9C", stopOpacity: 0.8 }} />
                <stop offset="100%" style={{ stopColor: "#95F985", stopOpacity: 0.8 }} />
              </linearGradient>
            </defs>
          </svg>
        </div>
        
        <div className="relative py-8">
          <ProductGrid />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
            <div className="bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-8">
              <div className="text-center max-w-2xl mx-auto">
                <h2 className="text-3xl font-serif font-light text-jewelry-text mb-6">Volg ons op WhatsApp!</h2>
                <p className="text-jewelry-text mb-8">
                  Blijf op de hoogte van onze nieuwste sieraden en updates via ons WhatsApp kanaal
                </p>
                <div className="flex flex-col items-center space-y-4">
                  {isMobile ? <Button className="bg-[#25D366] hover:bg-[#128C7E] text-white font-medium px-8 py-3 rounded-full shadow-lg transition-colors" onClick={() => window.open(whatsappChannelUrl, '_blank')}>Volg ons op WhatsApp</Button> : <>
                      <QRCode value={whatsappChannelUrl} size={200} level="H" includeMargin={true} className="mx-auto" />
                      <p className="text-sm text-jewelry-text">
                        Scan deze QR-code om ons te volgen op WhatsApp
                      </p>
                    </>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
};
export default Index;
